import {
  NOTEBOOK_PAGE_GAP,
  NOTEBOOK_PAGE_HEIGHT,
  NOTEBOOK_PAGE_WIDTH,
  NOTEBOOK_STYLE,
} from "@excalidraw/common";
import { newFrameElement } from "@excalidraw/element";
import { exportToCanvas } from "@excalidraw/excalidraw";

import type { NonDeletedExcalidrawElement } from "@excalidraw/element/types";
import type { AppState, BinaryFiles } from "@excalidraw/excalidraw/types";

import type { CanvasScene } from "./libraryApi";

// jspdf + fflate are loaded on demand (only when exporting) so they stay out of
// the main bundle and don't bloat first load / the PWA precache.
type JsPDFCtor = typeof import("jspdf").jsPDF;

// Thrown when there is nothing to export (empty canvas / empty library).
export const EMPTY_EXPORT = "EMPTY_EXPORT";

// Cap the longest rendered side so huge boards don't blow up memory; the whole
// drawing still lands on a single page, just scaled to fit.
const MAX_PDF_SIDE = 8000;

const safeName = (name: string) =>
  (name || "pizarra").replace(/[\\/:*?"<>|]+/g, "_").trim() || "pizarra";

const sceneToCanvas = (
  elements: readonly NonDeletedExcalidrawElement[],
  appState: Partial<AppState> | undefined,
  files: BinaryFiles | null,
): Promise<HTMLCanvasElement> =>
  exportToCanvas({
    elements,
    appState: {
      viewBackgroundColor: "#ffffff",
      ...appState,
      exportBackground: true,
      exportWithDarkMode: false,
      exportScale: 1,
    },
    files: files ?? null,
    exportPadding: 16,
    getDimensions: (width, height) => {
      const longest = Math.max(width, height) || 1;
      const scale = Math.min(2, MAX_PDF_SIDE / longest);
      const s = scale > 0 ? scale : 1;
      return {
        width: Math.round(width * s),
        height: Math.round(height * s),
        scale: s,
      };
    },
  });

const canvasToPdf = (canvas: HTMLCanvasElement, JsPDF: JsPDFCtor) => {
  const w = canvas.width;
  const h = canvas.height;
  const pdf = new JsPDF({
    orientation: w >= h ? "landscape" : "portrait",
    unit: "px",
    format: [w, h],
    hotfixes: ["px_scaling"],
  });
  pdf.addImage(canvas.toDataURL("image/png"), "PNG", 0, 0, w, h);
  return pdf;
};

// --- Notebook (A4 paged) export -------------------------------------------
// Each notebook page becomes one A4 PDF page: content is rendered clipped to
// the page rect (anything outside the sheet is cut off) on top of the page's
// own background pattern. We render the content via a synthetic frame so
// Excalidraw's frame-export clipping does the cropping for us.
const NOTEBOOK_PDF_SCALE = 2;
const A4_MM = { w: 210, h: 297 };

const drawNotebookPattern = (
  ctx: CanvasRenderingContext2D,
  style: string,
  W: number,
  H: number,
  scale: number,
  opacity: number,
) => {
  const alpha = Math.max(0, Math.min(1, opacity / 100));
  if (style === "blank" || alpha === 0) {
    return;
  }
  ctx.save();
  ctx.globalAlpha = alpha;
  const pad = NOTEBOOK_STYLE.padding * scale;
  ctx.strokeStyle = NOTEBOOK_STYLE.patternColor;
  ctx.fillStyle = NOTEBOOK_STYLE.patternColor;
  ctx.lineWidth = Math.max(1, scale);
  if (style === "ruled") {
    for (let y = pad; y < H; y += NOTEBOOK_STYLE.lineGap * scale) {
      ctx.beginPath();
      ctx.moveTo(pad / 2, y);
      ctx.lineTo(W - pad / 2, y);
      ctx.stroke();
    }
  } else if (style === "grid") {
    const g = NOTEBOOK_STYLE.gridGap * scale;
    for (let x = pad; x < W - pad / 2; x += g) {
      ctx.beginPath();
      ctx.moveTo(x, pad);
      ctx.lineTo(x, H - pad);
      ctx.stroke();
    }
    for (let y = pad; y < H - pad / 2; y += g) {
      ctx.beginPath();
      ctx.moveTo(pad, y);
      ctx.lineTo(W - pad, y);
      ctx.stroke();
    }
  } else if (style === "dotted") {
    const g = NOTEBOOK_STYLE.dotGap * scale;
    const r = 1.2 * scale;
    for (let x = pad; x < W - pad / 2; x += g) {
      for (let y = pad; y < H - pad / 2; y += g) {
        ctx.beginPath();
        ctx.arc(x, y, r, 0, Math.PI * 2);
        ctx.fill();
      }
    }
  }
  ctx.restore();
};

const buildNotebookPdf = async (
  JsPDF: JsPDFCtor,
  elements: readonly NonDeletedExcalidrawElement[],
  appState: Partial<AppState> | undefined,
  files: BinaryFiles | null,
) => {
  const pageCount = Math.max(
    1,
    Math.floor((appState?.notebookPageCount as number) ?? 1),
  );
  const style = (appState?.notebookPageStyle as string) ?? "ruled";
  const opacity = (appState?.notebookPageOpacity as number) ?? 100;
  const stride = NOTEBOOK_PAGE_HEIGHT + NOTEBOOK_PAGE_GAP;
  const scale = NOTEBOOK_PDF_SCALE;
  const W = Math.round(NOTEBOOK_PAGE_WIDTH * scale);
  const H = Math.round(NOTEBOOK_PAGE_HEIGHT * scale);

  const pdf = new JsPDF({ orientation: "portrait", unit: "mm", format: "a4" });

  for (let i = 0; i < pageCount; i++) {
    const frame = newFrameElement({
      x: 0,
      y: i * stride,
      width: NOTEBOOK_PAGE_WIDTH,
      height: NOTEBOOK_PAGE_HEIGHT,
    });

    // Render the page content with a transparent background, clipped to the
    // sheet via the synthetic frame (overflow is cut off).
    let contentCanvas: HTMLCanvasElement | null = null;
    try {
      contentCanvas = await exportToCanvas({
        elements: [...elements, frame],
        appState: {
          ...appState,
          notebookMode: false,
          viewBackgroundColor: "#ffffff",
          exportBackground: false,
          exportWithDarkMode: false,
          exportScale: scale,
        },
        files: files ?? null,
        exportPadding: 0,
        exportingFrame: frame,
        getDimensions: () => ({ width: W, height: H, scale }),
      });
    } catch {
      contentCanvas = null; // empty page → just the blank sheet + pattern
    }

    // Compose the final A4 page: white sheet + pattern + content on top.
    const page = document.createElement("canvas");
    page.width = W;
    page.height = H;
    const ctx = page.getContext("2d")!;
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, W, H);
    drawNotebookPattern(ctx, style, W, H, scale, opacity);
    if (contentCanvas) {
      ctx.drawImage(contentCanvas, 0, 0, W, H);
    }

    if (i > 0) {
      pdf.addPage("a4", "portrait");
    }
    pdf.addImage(page.toDataURL("image/png"), "PNG", 0, 0, A4_MM.w, A4_MM.h);
  }
  return pdf;
};

/** Export the current scene as a single-page PDF sized to the whole drawing. */
export const exportSceneToPdf = async (
  elements: readonly NonDeletedExcalidrawElement[],
  appState: Partial<AppState> | undefined,
  files: BinaryFiles | null,
  name: string,
): Promise<void> => {
  const isNotebook = !!appState?.notebookMode;
  if (!isNotebook && (!elements || elements.length === 0)) {
    throw new Error(EMPTY_EXPORT);
  }
  const { jsPDF } = await import("jspdf");
  const pdf = isNotebook
    ? await buildNotebookPdf(jsPDF, elements ?? [], appState, files)
    : canvasToPdf(await sceneToCanvas(elements, appState, files), jsPDF);
  const blob = pdf.output("blob");
  await saveOrShare(blob, `${safeName(name)}.pdf`, "application/pdf", name);
};

const downloadBlob = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

/**
 * On mobile/PWA, use the native share sheet (Web Share API with files) so the
 * user can pick where to save ("Save to Files", Drive, send to an app…) instead
 * of a download landing in a hard-to-find folder. Falls back to a download on
 * desktop or when sharing files isn't supported / is cancelled.
 */
const saveOrShare = async (
  blob: Blob,
  filename: string,
  mimeType: string,
  title: string,
) => {
  const nav = navigator as Navigator & {
    canShare?: (data?: { files?: File[] }) => boolean;
    share?: (data: { files?: File[]; title?: string }) => Promise<void>;
  };
  try {
    const file = new File([blob], filename, { type: mimeType });
    if (nav.canShare?.({ files: [file] }) && nav.share) {
      await nav.share({ files: [file], title });
      return;
    }
  } catch (e) {
    // user dismissed the share sheet → don't also download
    if (e instanceof Error && e.name === "AbortError") {
      return;
    }
    // otherwise fall through to a normal download
  }
  downloadBlob(blob, filename);
};

/** Render every canvas to a PDF and bundle them into a single .zip download. */
export const exportAllToPdfZip = async (
  items: { name: string; scene: CanvasScene }[],
): Promise<number> => {
  const { jsPDF } = await import("jspdf");
  const { zipSync } = await import("fflate");
  const files: Record<string, Uint8Array> = {};
  const used = new Set<string>();
  for (const item of items) {
    const elements = (item.scene.elements ??
      []) as readonly NonDeletedExcalidrawElement[];
    const appState = item.scene.appState as Partial<AppState>;
    const sceneFiles = item.scene.files as unknown as BinaryFiles;
    const isNotebook = !!appState?.notebookMode;
    if (!isNotebook && elements.length === 0) {
      continue;
    }
    const pdf = isNotebook
      ? await buildNotebookPdf(jsPDF, elements, appState, sceneFiles)
      : canvasToPdf(await sceneToCanvas(elements, appState, sceneFiles), jsPDF);
    const buf = new Uint8Array(pdf.output("arraybuffer"));
    let fname = `${safeName(item.name)}.pdf`;
    let i = 1;
    while (used.has(fname)) {
      fname = `${safeName(item.name)} (${i++}).pdf`;
    }
    used.add(fname);
    files[fname] = buf;
  }
  const count = Object.keys(files).length;
  if (count === 0) {
    throw new Error(EMPTY_EXPORT);
  }
  await saveOrShare(
    new Blob([zipSync(files)], { type: "application/zip" }),
    "pizzarweb-pdfs.zip",
    "application/zip",
    "PizzarWeb PDFs",
  );
  return count;
};

/** Save the backup bundle (share sheet on mobile, download on desktop). */
export const downloadJson = async (data: unknown, filename: string) => {
  await saveOrShare(
    new Blob([JSON.stringify(data)], { type: "application/json" }),
    filename,
    "application/json",
    "PizzarWeb backup",
  );
};
